import { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { AppLayout } from "@/components/layout/AppLayout";
import { useProcurement } from "@/context/ProcurementContext";
import { useAlerts } from "@/context/AlertContext";
import { POCreationWizard } from "@/components/purchaseOrder/POCreationWizard";
import POEditModal from "@/components/purchaseOrder/POEditModal";
import { PurchaseOrder } from "@/data/mockData";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Eye, 
  Edit, 
  SquarePen,
  Trash2, 
  Calendar, 
  DollarSign,
  Clock,
  CheckCircle,
  Building2,
  Package,
  AlertTriangle,
  RefreshCw,
  FileText,
  ChevronDown,
  Check,
  Receipt,
  MapPin,
  User,
  Users,
  Warehouse,
  XCircle
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatCurrency } from "@/lib/utils";
import { toast } from "sonner";

const PurchaseOrders = () => {
  const navigate = useNavigate();
  const { state, actions } = useProcurement();
  const { addAlert, getAlertsBySection, resolveAlert } = useAlerts();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateFilter, setDateFilter] = useState("Last 30 Days");
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [editingPO, setEditingPO] = useState<number | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPO, setSelectedPO] = useState<any>(null);
  const [showAllPOs, setShowAllPOs] = useState(false);
  const [showPODetails, setShowPODetails] = useState(false);
  const [selectedPODetails, setSelectedPODetails] = useState<any>(null);

  useEffect(() => {
    // Only fetch if data is not already loaded
    if (state.purchaseOrders.length === 0) {
      actions.fetchPurchaseOrders();
    }
    if (state.skus.length === 0) {
      actions.fetchSKUs();
    }
    if (state.vendors.length === 0) {
      actions.fetchVendors();
    }
  }, []);

  // Date filtering function
  const getDateFilteredPOs = (pos: any[]) => {
    if (!showAllPOs && dateFilter === "Last 30 Days") {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      return pos.filter(po => {
        if (!po.createdAt) return false;
        const poDate = new Date(po.createdAt);
        return poDate >= thirtyDaysAgo;
      });
    }
    return pos;
  };

  const filteredPOs = getDateFilteredPOs((state.purchaseOrders || []))
    .filter(po => {
      // Add null safety checks
      if (!po || !po.poNumber || !po.status) {
        console.warn('Invalid PO data:', po);
        return false;
      }

      const matchesSearch = 
        po.poNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (state.vendors?.find(v => v.id === po.vendorId)?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        po.status.toLowerCase().includes(searchTerm.toLowerCase());
      
      // If "All" is selected, show all POs that match search
      if (statusFilter === "All") {
        return matchesSearch;
      }
      
      // For specific status filters, only show the allowed statuses
      const allowedStatuses = [
        'CREATED', 
        'APPROVED', 
        'REJECTED', 
        'NEEDS_REVISION',
        'FULLY_RECEIVED', 
        'PARTLY_RECEIVED', 
        'FULLY_REJECTED'
      ];
      
      const matchesStatus = allowedStatuses.includes(po.status) && po.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateB - dateA;
    }); // Sort by newest first with null safety

  // Separate rejected POs for the rejected tab
  const rejectedPOs = getDateFilteredPOs((state.purchaseOrders || []))
    .filter(po => {
      if (!po || !po.poNumber || !po.status) return false;
      
      const matchesSearch = 
        po.poNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (state.vendors?.find(v => v.id === po.vendorId)?.name || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesSearch && (po.status === 'REJECTED' || po.status === 'FULLY_REJECTED');
    })
    .sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateB - dateA;
    });

  const getStatusVariant = (status: string) => {
    switch (status) {
      case "CREATED":
        return "outline";
      case "APPROVED":
        return "default";
      case "REJECTED":
        return "destructive";
      case "NEEDS_REVISION":
        return "secondary";
      case "FULLY_RECEIVED":
        return "default";
      case "PARTLY_RECEIVED":
        return "secondary";
      case "FULLY_REJECTED":
        return "destructive";
      default:
        return "outline";
    }
  };

  const handleDeletePO = async (poId: number) => {
    if (window.confirm("Are you sure you want to delete this purchase order?")) {
      const success = await actions.deletePurchaseOrder(poId);
      if (success) {
        toast.success("Purchase order deleted successfully");
      } else {
        toast.error("Failed to delete purchase order");
      }
    }
  };

  const handleEditPO = (po: any) => {
    setSelectedPO(po);
    setShowEditModal(true);
  };

  const handleViewPODetails = (po: any) => {
    setSelectedPODetails(po);
    setShowPODetails(true);
  };

  const handleSavePO = async (updatedData: any) => {
    try {
      const success = await actions.updatePurchaseOrder(updatedData.id, updatedData);
      if (success) {
        toast.success("PO updated successfully");
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error saving PO:", error);
      return false;
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("All");
    setDateFilter("Last 30 Days");
    setShowAllPOs(false);
  };

  const totalValue = (state.purchaseOrders || []).reduce((sum, po) => {
    return sum + (po?.totalAmount || 0);
  }, 0);
  const totalPOs = (state.purchaseOrders || []).length;

  // Reusable PO Card Component
  const renderPOCard = (po: any) => {
    const vendor = state.vendors?.find(v => v && v.id === po.vendorId);
    return (
      <div key={po.id} className="border rounded-lg p-4 hover:bg-muted/50 transition-colors">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="font-semibold text-lg">{po?.poNumber || 'Unknown PO'}</h3>
              {po?.status === 'APPROVED' ? (
                <span className="badge bg-success text-white px-2 py-1 rounded text-sm">
                  {po?.status || 'Unknown'}
                </span>
              ) : (
                <Badge variant={getStatusVariant(po?.status || 'Unknown')}>
                  {po?.status || 'Unknown'}
                </Badge>
              )}
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">Vendor:</span>
                <span>{vendor?.name || 'Unknown'}</span>
              </div>
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">Amount:</span>
                <span>{formatCurrency(po?.totalAmount || 0)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">Items:</span>
                <span>{po.items?.length || 0} items</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">Delivery Date:</span>
                <span>{po?.deliveryDetails?.expectedDate ? new Date(po.deliveryDetails.expectedDate).toLocaleDateString() : 'N/A'}</span>
              </div>
            </div>

            <div className="mt-3">
              <div className="text-sm text-muted-foreground mb-2">Items:</div>
              <div className="space-y-1">
                {(po?.items || []).map((item, index) => {
                  const sku = state.skus?.find(s => s && s.id === item.skuId);
                  return (
                    <div key={item.id || index} className="flex justify-between items-center text-sm">
                      <div className="flex items-center gap-2">
                        <Package className="h-3 w-3 text-muted-foreground" />
                        <span>{sku?.name || `SKU ${item?.skuId || 'Unknown'}`}</span>
                        <span className="text-muted-foreground">({sku?.skuCode || `SKU-${item?.skuId || 'Unknown'}`})</span>
                      </div>
                      <div className="text-right">
                        <span className="font-medium">Qty: {item?.quantity || 0} × {formatCurrency(item?.unitPrice || 0)} = {formatCurrency(item?.totalPrice || 0)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center gap-4 text-xs text-muted-foreground mt-3">
              <span>Created: {po?.createdAt ? new Date(po.createdAt).toLocaleDateString() : 'N/A'}</span>
              <span>Updated: {po?.updatedAt ? new Date(po.updatedAt).toLocaleDateString() : 'N/A'}</span>
              {po?.notes && <span className="truncate max-w-xs">Notes: {po.notes}</span>}
            </div>
            
            {/* Show approval remarks for POs that need revision */}
            {po?.status === 'NEEDS_REVISION' && po?.approvalRemarks && (
              <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-xs">
                <div className="flex items-start gap-2">
                  <div className="w-4 h-4 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs">!</span>
                  </div>
                  <div>
                    <p className="font-medium text-orange-800">Approval Remarks:</p>
                    <p className="text-orange-700 mt-1">{po.approvalRemarks}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Show rejection reason for rejected POs */}
            {(po?.status === 'REJECTED' || po?.status === 'FULLY_REJECTED') && po?.rejectionReason && (
              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs">
                <div className="flex items-start gap-2">
                  <div className="w-4 h-4 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-xs">×</span>
                  </div>
                  <div>
                    <p className="font-medium text-red-800">Rejection Reason:</p>
                    <p className="text-red-700 mt-1">{po.rejectionReason}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 ml-4">
            <Button 
              variant="ghost" 
              size="sm" 
              title="View Details"
              onClick={() => handleViewPODetails(po)}
            >
              <Eye className="h-4 w-4" />
            </Button>
            
            {/* Send for Approval - For CREATED status */}
            {po.status === 'CREATED' && (
              <Button 
                variant="outline" 
                size="sm" 
                className="text-blue-600 border-blue-200 hover:bg-blue-50"
                onClick={() => navigate(`/purchase-orders/approve/${po.id}`)}
                title="Send for Approval"
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Send for Approval
              </Button>
            )}
            
            {/* Resubmit for Approval - For NEEDS_REVISION status */}
            {po.status === 'NEEDS_REVISION' && (
              <Button 
                variant="outline" 
                size="sm" 
                className="text-orange-600 border-orange-200 hover:bg-orange-50"
                onClick={() => navigate(`/purchase-orders/approve/${po.id}`)}
                title="Resubmit for Approval"
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Resubmit
              </Button>
            )}
            
            {/* Edit button - Only enabled for NEEDS_REVISION status */}
            {po.status === 'NEEDS_REVISION' ? (
              <Button 
                variant="outline" 
                size="sm" 
                className="text-blue-600 border-blue-200 hover:bg-blue-50"
                onClick={() => handleEditPO(po)}
                title="Edit PO"
              >
                <SquarePen className="h-4 w-4" />
              </Button>
            ) : (
              <Button 
                variant="ghost" 
                size="sm" 
                title="Edit PO (Disabled)"
                disabled
              >
                <SquarePen className="h-4 w-4" />
              </Button>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-red-500 hover:text-red-700"
              onClick={() => handleDeletePO(po.id)}
              title="Delete PO"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  if (showCreateWizard) {
    return (
      <AppLayout>
        <POCreationWizard 
          onSave={async (poData) => {
            // Handle PO creation
            const success = await actions.createPurchaseOrder(poData as PurchaseOrder);
            if (success) {
              setShowCreateWizard(false);
              toast.success("Purchase order created successfully");
              return true;
            }
            return false;
          }}
          onCancel={() => setShowCreateWizard(false)}
        />
      </AppLayout>
    );
  }

  if (editingPO) {
    const poToEdit = state.purchaseOrders.find(po => po.id === editingPO);
    return (
      <AppLayout>
        <POCreationWizard 
          onSave={async (poData) => {
            // Handle PO update
            const success = await actions.updatePurchaseOrder(editingPO, poData);
            if (success) {
              setEditingPO(null);
              toast.success("Purchase order updated successfully");
              return true;
            }
            return false;
          }}
          onCancel={() => setEditingPO(null)}
        />
      </AppLayout>
    );
  }

  // Get alerts related to purchase orders
  const poAlerts = getAlertsBySection('purchase-orders').filter(alert => !alert.resolved);

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Purchase Orders</h1>
            <p className="text-muted-foreground">Create and manage purchase orders</p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={async () => {
                try {
                  await actions.fetchPurchaseOrders();
                  toast.success("Purchase orders refreshed");
                } catch (error) {
                  toast.error("Failed to refresh data");
                }
              }}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <Button onClick={() => setShowCreateWizard(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create
            </Button>
          </div>
        </div>

        {/* Alerts Section */}
        {poAlerts.length > 0 && (
          <div className="space-y-2">
            {poAlerts.map(alert => (
              <div key={alert.id} className={`p-3 rounded-lg border ${
                alert.severity === 'critical' ? 'bg-red-50 border-red-200' :
                alert.severity === 'high' ? 'bg-orange-50 border-orange-200' :
                'bg-yellow-50 border-yellow-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className={`h-4 w-4 ${
                      alert.severity === 'critical' ? 'text-red-600' :
                      alert.severity === 'high' ? 'text-orange-600' :
                      'text-yellow-600'
                    }`} />
                    <span className={`text-sm font-medium ${
                      alert.severity === 'critical' ? 'text-red-800' :
                      alert.severity === 'high' ? 'text-orange-800' :
                      'text-yellow-800'
                    }`}>
                      {alert.message}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => resolveAlert(alert.id)}
                    className="text-xs"
                  >
                    Resolve
                  </Button>
                </div>
                {alert.data && (
                  <div className="mt-2 text-xs text-gray-600">
                    Invoice: {alert.invoiceId} | PO: {alert.poId}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Search and Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder="Search POs by ID, vendor, or status..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="flex items-center gap-2">
                      <Filter className="h-4 w-4" />
                      Status: {statusFilter === "All" ? "All" : statusFilter}
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56">
                    <DropdownMenuItem onClick={() => setStatusFilter("All")}>
                      <Check className={`h-4 w-4 mr-2 ${statusFilter === "All" ? "opacity-100" : "opacity-0"}`} />
                      All Statuses
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      📋 PRE-APPROVAL
                    </DropdownMenuLabel>
                    <DropdownMenuItem onClick={() => setStatusFilter("CREATED")}>
                      <Check className={`h-4 w-4 mr-2 ${statusFilter === "CREATED" ? "opacity-100" : "opacity-0"}`} />
                      Created
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setStatusFilter("APPROVED")}>
                      <Check className={`h-4 w-4 mr-2 ${statusFilter === "APPROVED" ? "opacity-100" : "opacity-0"}`} />
                      Approved
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setStatusFilter("REJECTED")}>
                      <Check className={`h-4 w-4 mr-2 ${statusFilter === "REJECTED" ? "opacity-100" : "opacity-0"}`} />
                      Rejected
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setStatusFilter("NEEDS_REVISION")}>
                      <Check className={`h-4 w-4 mr-2 ${statusFilter === "NEEDS_REVISION" ? "opacity-100" : "opacity-0"}`} />
                      Needs Revision
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      📦 POST-APPROVAL
                    </DropdownMenuLabel>
                    <DropdownMenuItem onClick={() => setStatusFilter("FULLY_RECEIVED")}>
                      <Check className={`h-4 w-4 mr-2 ${statusFilter === "FULLY_RECEIVED" ? "opacity-100" : "opacity-0"}`} />
                      Received
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setStatusFilter("PARTLY_RECEIVED")}>
                      <Check className={`h-4 w-4 mr-2 ${statusFilter === "PARTLY_RECEIVED" ? "opacity-100" : "opacity-0"}`} />
                      Partly Received
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setStatusFilter("FULLY_REJECTED")}>
                      <Check className={`h-4 w-4 mr-2 ${statusFilter === "FULLY_REJECTED" ? "opacity-100" : "opacity-0"}`} />
                      Fully Rejected
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="px-3 py-2 border border-input bg-background rounded-md text-sm"
                >
                  <option value="Last 30 Days">Last 30 Days</option>
                  <option value="All">Show All</option>
                </select>
                {dateFilter === "Last 30 Days" && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowAllPOs(!showAllPOs)}
                    className="text-blue-600 border-blue-200 hover:bg-blue-50"
                  >
                    {showAllPOs ? "Show Recent" : "Show All POs"}
                  </Button>
                )}
                <Button variant="outline" onClick={clearFilters}>
                  Clear
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <DollarSign className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-muted-foreground">Total Value</p>
                  <p className="text-2xl font-bold">{formatCurrency(totalValue)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-muted-foreground">Total POs</p>
                  <p className="text-2xl font-bold">{totalPOs}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Purchase Orders with Tabs */}
        <Tabs defaultValue="all-pos" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="all-pos" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              All POs ({filteredPOs.length})
            </TabsTrigger>
            <TabsTrigger value="rejected-pos" className="flex items-center gap-2">
              <XCircle className="h-4 w-4" />
              REJECTED POs ({rejectedPOs.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all-pos" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Purchase Orders</CardTitle>
                <CardDescription>
                  {filteredPOs.length} of {state.purchaseOrders.length} purchase orders
                  {dateFilter === "Last 30 Days" && !showAllPOs && " (Last 30 days)"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {state.isLoading ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Loading purchase orders...
                  </div>
                ) : filteredPOs.length > 0 ? (
                  <div className="space-y-4">
                    {filteredPOs.map((po) => renderPOCard(po))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No purchase orders found</p>
                    <p className="text-sm">Create your first purchase order to get started</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="rejected-pos" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>REJECTED Purchase Orders</CardTitle>
                <CardDescription>
                  {rejectedPOs.length} rejected purchase orders
                  {dateFilter === "Last 30 Days" && !showAllPOs && " (Last 30 days)"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {rejectedPOs.length > 0 ? (
                  <div className="space-y-4">
                    {rejectedPOs.map((po) => renderPOCard(po))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <XCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No rejected purchase orders found</p>
                    <p className="text-sm">Rejected POs will appear here</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* PO Edit Modal */}
        {showEditModal && selectedPO && (
          <POEditModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setSelectedPO(null);
            }}
            poData={selectedPO}
            remarks={selectedPO.approvalRemarks || 'No specific remarks provided'}
            onSave={handleSavePO}
            vendors={state.vendors}
            skus={state.skus}
          />
        )}
      </div>

      {/* PO Details Modal */}
      <Dialog open={showPODetails} onOpenChange={setShowPODetails}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Purchase Order Details - {selectedPODetails?.poNumber || 'Unknown'}
            </DialogTitle>
            <DialogDescription>
              Complete information about this purchase order including invoices, items, and handover details
            </DialogDescription>
          </DialogHeader>

          {selectedPODetails && (
            <div className="space-y-6">
              {/* PO Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Vendor:</span>
                    <span>{state.vendors?.find(v => v.id === selectedPODetails.vendorId)?.name || 'Unknown'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Total Amount:</span>
                    <span className="font-semibold">{formatCurrency(selectedPODetails.totalAmount || 0)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Expected Delivery:</span>
                    <span>{selectedPODetails.deliveryDetails?.expectedDate ? new Date(selectedPODetails.deliveryDetails.expectedDate).toLocaleDateString() : 'N/A'}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge variant={getStatusVariant(selectedPODetails.status)}>
                      {selectedPODetails.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Created:</span>
                    <span>{selectedPODetails.createdAt ? new Date(selectedPODetails.createdAt).toLocaleDateString() : 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Updated:</span>
                    <span>{selectedPODetails.updatedAt ? new Date(selectedPODetails.updatedAt).toLocaleDateString() : 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* PO Items */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  PO Items ({selectedPODetails.items?.length || 0})
                </h3>
                <div className="space-y-2">
                  {selectedPODetails.items?.map((item: any, index: number) => {
                    const sku = state.skus?.find(s => s.id === item.skuId);
                    return (
                      <div key={item.id || index} className="flex justify-between items-center p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <Package className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <span className="font-medium">{sku?.name || `SKU ${item.skuId}`}</span>
                            <span className="text-muted-foreground ml-2">({sku?.skuCode || `SKU-${item.skuId}`})</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium">Qty: {item.quantity} × {formatCurrency(item.unitPrice)}</div>
                          <div className="font-semibold text-green-600">{formatCurrency(item.totalPrice)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Associated Invoices */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Receipt className="h-5 w-5" />
                  Associated Invoices ({state.invoices?.filter(inv => inv.poNumber === selectedPODetails.poNumber && state.purchaseOrders.some(po => po.poNumber === selectedPODetails.poNumber)).length || 0})
                </h3>
                {state.invoices?.filter(inv => inv.poNumber === selectedPODetails.poNumber && state.purchaseOrders.some(po => po.poNumber === selectedPODetails.poNumber)).length > 0 ? (
                  <div className="space-y-3">
                    {state.invoices?.filter(inv => inv.poNumber === selectedPODetails.poNumber && state.purchaseOrders.some(po => po.poNumber === selectedPODetails.poNumber)).map((invoice: any) => (
                      <div key={invoice.id} className="p-4 border rounded-lg bg-blue-50">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center gap-2">
                            <Receipt className="h-4 w-4 text-blue-600" />
                            <span className="font-semibold">{invoice.invoiceNumber}</span>
                            <Badge variant={getStatusVariant(invoice.status)}>{invoice.status}</Badge>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-blue-600">{formatCurrency(invoice.totalAmount)}</div>
                            <div className="text-sm text-muted-foreground">{new Date(invoice.createdAt).toLocaleDateString()}</div>
                          </div>
                        </div>
                        
                        {/* Invoice Items */}
                        <div className="mt-3 space-y-2">
                          <h4 className="text-sm font-medium text-gray-700">Invoice Items:</h4>
                          {invoice.items?.map((item: any, index: number) => {
                            const sku = state.skus?.find(s => s.id === item.skuId);
                            return (
                              <div key={index} className="flex justify-between items-center text-sm bg-white p-2 rounded border">
                                <span>{sku?.name || `SKU ${item.skuId}`}</span>
                                <span>Qty: {item.quantity} × {formatCurrency(item.unitPrice)} = {formatCurrency(item.totalPrice)}</span>
                              </div>
                            );
                          })}
                        </div>

                        {/* Received Status */}
                        <div className="mt-3 flex items-center gap-4 text-sm">
                          <div className="flex items-center gap-2">
                            <Package className="h-4 w-4 text-muted-foreground" />
                            <span>Received Status:</span>
                            <Badge variant={invoice.receivedStatus === 'Received' ? 'default' : 'secondary'}>
                              {invoice.receivedStatus || 'Not Received'}
                            </Badge>
                          </div>
                          {invoice.qualityTested && (
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              <span className="text-green-600">Quality Tested</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No invoices created for this PO yet</p>
                  </div>
                )}
              </div>

              {/* Storage Locations & Handover Details */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Warehouse className="h-5 w-5" />
                  Storage & Handover Information
                </h3>
                
                {/* Check if any invoices have handover logs */}
                {state.invoices?.filter(inv => inv.poNumber === selectedPODetails.poNumber).some((inv: any) => inv.handoverLogs?.length > 0) ? (
                  <div className="space-y-3">
                    {state.invoices?.filter(inv => inv.poNumber === selectedPODetails.poNumber).map((invoice: any) => 
                      invoice.handoverLogs?.map((handover: any, index: number) => (
                        <div key={index} className="p-4 border rounded-lg bg-green-50">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-green-600" />
                              <span className="font-semibold">Handed Over to: {handover.floorManagerName}</span>
                            </div>
                            <Badge variant="outline" className="text-green-600 border-green-200">
                              {handover.status}
                            </Badge>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3 text-sm">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <MapPin className="h-4 w-4 text-muted-foreground" />
                                <span>Storage Location:</span>
                                <span className="font-medium">{handover.storageLocation}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-muted-foreground" />
                                <span>Handover Date:</span>
                                <span>{new Date(handover.handoverDate).toLocaleDateString()}</span>
                              </div>
                            </div>
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <Users className="h-4 w-4 text-muted-foreground" />
                                <span>Contact:</span>
                                <span>{handover.floorManagerContact}</span>
                              </div>
                              {handover.notes && (
                                <div className="flex items-start gap-2">
                                  <FileText className="h-4 w-4 text-muted-foreground mt-0.5" />
                                  <span>Notes:</span>
                                  <span className="text-muted-foreground">{handover.notes}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Handover Items */}
                          {handover.items && (
                            <div className="mt-3">
                              <h4 className="text-sm font-medium text-gray-700 mb-2">Items Handed Over:</h4>
                              <div className="flex flex-wrap gap-2">
                                {Object.entries(handover.items).map(([key, value]: [string, any]) => 
                                  value && (
                                    <Badge key={key} variant="outline" className="text-green-600 border-green-200">
                                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                    </Badge>
                                  )
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Warehouse className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No handover information available</p>
                    <p className="text-sm">Items will be tracked here once handed over to floor managers</p>
                  </div>
                )}
              </div>

              {/* PO Notes */}
              {selectedPODetails.notes && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Notes
                  </h3>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-muted-foreground">{selectedPODetails.notes}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};

export default PurchaseOrders;
